import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiArrowLeft, HiHeart, HiShare, HiCake, HiPlay, HiVolumeUp } from 'react-icons/hi';
import { supabase } from '../supabaseClient';

const BirthdayCardViewer = () => {
  const { cardId } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [password, setPassword] = useState('');
  const [enteredPassword, setEnteredPassword] = useState('');
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [creatorId, setCreatorId] = useState(null); // Add creator ID state

  useEffect(() => {
    // Get creator ID from localStorage
    const id = localStorage.getItem('birthdayCardCreatorId');
    setCreatorId(id);
  }, []);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        // Fetch the card from Supabase
        const { data, error } = await supabase
          .from('birthday_cards')
          .select('*')
          .eq('id', cardId)
          .single();

        if (error) {
          console.error('Error fetching card:', error);
          setError('Card not found or has expired.');
          setLoading(false);
          return;
        }

        // Check if card is private and user is not the creator
        if (data.is_private && data.creator_id && creatorId !== data.creator_id) {
          // For private cards, we still allow access via direct link but show a message
          console.log('This is a private card. Only the creator can see it on the birthday page.');
        }

        // Check if card requires password
        if (data.password) {
          setPasswordRequired(true);
          setPassword(data.password);
          setLoading(false);
          return;
        }

        // Check if card has expired
        if (data.expires_at && new Date(data.expires_at) < new Date() && !data.is_permanent) {
          setError('This birthday card has expired.');
          setLoading(false);
          return;
        }

        setCard(data);
        setLikeCount(data.likes || 0);
        setLoading(false);
        
        // Update view count
        await supabase
          .from('birthday_cards')
          .update({ views: (data.views || 0) + 1 })
          .eq('id', cardId);
      } catch (err) {
        console.error('Error:', err);
        setError('An error occurred while loading the card.');
        setLoading(false);
      }
    };

    if (cardId) {
      fetchCard();
    }
  }, [cardId, creatorId]);

  const handleLike = async () => {
    if (liked) return;
    
    try {
      const newLikeCount = likeCount + 1;
      setLikeCount(newLikeCount);
      setLiked(true);
      
      // Update like count in Supabase
      await supabase
        .from('birthday_cards')
        .update({ likes: newLikeCount })
        .eq('id', cardId);
    } catch (err) {
      console.error('Error liking card:', err);
      // Revert on error
      setLikeCount(likeCount);
      setLiked(false);
    }
  };

  const verifyPassword = () => {
    if (enteredPassword === password) {
      // Password is correct, fetch the full card
      const fetchFullCard = async () => {
        try {
          setLoading(true);
          const { data, error } = await supabase
            .from('birthday_cards')
            .select('*')
            .eq('id', cardId)
            .single();

          if (error) {
            console.error('Error fetching card:', error);
            setError('Card not found or has expired.');
            setLoading(false);
            return;
          }

          // Check if card has expired
          if (data.expires_at && new Date(data.expires_at) < new Date() && !data.is_permanent) {
            setError('This birthday card has expired.');
            setLoading(false);
            return;
          }

          setCard(data);
          setPasswordRequired(false);
          setLikeCount(data.likes || 0);
          setLoading(false);
          
          // Update view count
          await supabase
            .from('birthday_cards')
            .update({ views: (data.views || 0) + 1 })
            .eq('id', cardId);
        } catch (err) {
          console.error('Error:', err);
          setError('An error occurred while loading the card.');
          setLoading(false);
        }
      };

      fetchFullCard();
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: card.title || 'Birthday Card',
          text: `Check out this birthday card for ${card.recipient_name}!`,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Sharing failed:', err);
        // Fallback to copy
        copyToClipboard(shareUrl);
      }
    } else {
      // Fallback to copy
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        alert('Link copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4"></div>
          <p className="text-gray-300">Loading birthday card...</p>
        </div>
      </div>
    );
  }

  // Show password input if password is required
  if (passwordRequired) {
    return (
      <div className="min-h-screen px-4 py-12 sm:px-6">
        <div className="max-w-md mx-auto">
          <div className="glass-morphism-strong rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-display-3 font-space-grotesk font-bold text-white mb-4">
              Password Protected
            </h1>
            <p className="text-gray-300 mb-6">
              This birthday card is protected with a password.
            </p>
            
            <div className="mb-6">
              <input
                type="password"
                value={enteredPassword}
                onChange={(e) => {
                  setEnteredPassword(e.target.value);
                  setPasswordError('');
                }}
                placeholder="Enter password"
                className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green mb-2"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    verifyPassword();
                  }
                }}
              />
              {passwordError && (
                <p className="text-red-400 text-sm mt-2">{passwordError}</p>
              )}
            </div>
            
            <button
              onClick={verifyPassword}
              className="w-full bg-gradient-to-r from-accent-green to-accent-blue text-white font-medium py-3 px-4 rounded-lg hover:from-accent-green/90 hover:to-accent-blue/90 transition-all duration-200 transform hover:scale-105"
            >
              Unlock Card
            </button>
            
            <div className="mt-6">
              <Link to="/birthdays" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors">
                <HiArrowLeft className="mr-2" />
                Back to Birthday Page
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen px-6">
        <div className="max-w-4xl mx-auto pt-20">
          <div className="mb-6">
            <Link to="/birthdays" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors">
              <HiArrowLeft className="mr-2" />
              Back to Birthday Page
            </Link>
          </div>
          <div className="glass-morphism-strong rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">😢</div>
            <h1 className="text-display-3 font-space-grotesk font-bold text-white mb-4">
              Card Not Available
            </h1>
            <p className="text-gray-300 text-xl mb-6">{error}</p>
            <Link to="/birthdays" className="btn-primary inline-block">
              Create Your Own Card
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen px-6">
        <div className="max-w-4xl mx-auto pt-20">
          <div className="mb-6">
            <Link to="/birthdays" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors">
              <HiArrowLeft className="mr-2" />
              Back to Birthday Page
            </Link>
          </div>
          <div className="glass-morphism-strong rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">❓</div>
            <h1 className="text-display-3 font-space-grotesk font-bold text-white mb-4">
              Card Not Found
            </h1>
            <p className="text-gray-300 text-xl mb-6">The birthday card you're looking for doesn't exist.</p>
            <Link to="/birthdays" className="btn-primary inline-block">
              Create Your Own Card
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Card styling based on user preferences
  const cardStyle = {
    backgroundColor: card.background_color || '#1a1a2e',
    color: card.text_color || '#ffffff',
    fontFamily: card.font_family || 'Space Grotesk, sans-serif'
  };

  return (
    <div className="min-h-screen px-4 py-12 sm:px-6"> {/* Changed px-6 to px-4 for mobile */}
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/birthdays" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors">
            <HiArrowLeft className="mr-2" />
            Back to Birthday Page
          </Link>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl overflow-hidden shadow-2xl bg-opacity-90"
          style={cardStyle}
        >
          {/* Private Card Indicator */}
          {card.is_private && (
            <div className="bg-purple-900/50 text-purple-200 text-center py-2 text-sm">
              This is a private card. Only the creator can see it on the birthday page.
            </div>
          )}
          
          {/* Card Header */}
          <div className="p-6 md:p-12 text-left sm:text-left"> {/* Changed to text-right for mobile, sm:text-left for larger screens */}
            <h1 className="text-3xl md:text-display-2 font-bold mb-4">
              {card.title || 'Happy Birthday!'}
            </h1>
            <p className="text-xl md:text-2xl mb-8">
              For <span className="font-bold">{card.recipient_name || 'Someone Special'}</span>
            </p>
            
            {/* Media Content */}
            {(card.card_images && card.card_images.length > 0) && (
              <div className="mb-8 text-left">
                {card.card_images.map((imageUrl, index) => (
                  <img 
                    key={index}
                    src={imageUrl} 
                    alt={`Birthday card ${index + 1}`} 
                    className="max-w-full h-auto rounded-2xl mx-auto sm:mx-0 shadow-lg mb-4"
                  />
                ))}
              </div>
            )}
          
            {(card.card_videos && card.card_videos.length > 0) && (
              <div className="mb-8 text-left">
                {card.card_videos.map((videoUrl, index) => (
                  <div key={index} className="relative pt-[56.25%] rounded-2xl overflow-hidden shadow-lg mx-auto sm:mx-0 mb-4">
                    <video 
                      src={videoUrl} 
                      controls
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                ))}
              </div>
            )}
          
            {(card.card_audios && card.card_audios.length > 0) && (
              <div className="mb-8 text-left">
                {card.card_audios.map((audioUrl, index) => (
                  <div key={index} className="bg-black/30 rounded-2xl p-4 backdrop-blur-sm border border-white/10 mx-auto sm:mx-0 mb-4">
                    <div className="flex items-center mb-3">
                      <HiVolumeUp className="w-5 h-5 mr-2 text-accent-green" />
                      <span className="text-sm font-medium">Audio Message {index + 1}</span>
                    </div>
                    <audio 
                      src={audioUrl} 
                      controls
                      className="w-full rounded-lg"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Card Message */}
          <div className="px-6 md:px-12 pb-8 md:pb-12 text-left"> {/* Added text-left */}
            <div className="bg-black/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg md:text-xl leading-relaxed whitespace-pre-wrap text-left"> {/* Added text-left */}
                  {card.message || 'No message provided.'}
                </p>
              </div>
            </div>
            
            {/* Sender */}
            <div className="text-left"> {/* Changed from text-center to text-left */}
              <p className="text-lg">
                With love from <span className="font-bold">{card.sender_name || 'A Friend'}</span>
              </p>
            </div>
          </div>
          
          {/* Card Footer with Actions */}
          <div className="bg-black/10 backdrop-blur-sm p-6 flex flex-col sm:flex-row justify-between items-start gap-4"> {/* Changed items-center to items-start */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLike}
                disabled={liked}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all ${
                  liked 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
              >
                <HiHeart className={liked ? 'fill-current' : ''} />
                <span>{likeCount} {likeCount === 1 ? 'Like' : 'Likes'}</span>
              </button>
              
              <button
                onClick={handleShare}
                className="flex items-center space-x-2 px-4 py-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-all"
              >
                <HiShare />
                <span>Share</span>
              </button>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start space-y-2 sm:space-y-0 sm:space-x-4"> {/* Changed items-center to items-start */}
              <div className="text-sm opacity-75 text-left sm:text-left"> {/* Changed text-center to text-left */}
                {card.is_permanent ? (
                  <span>Permanent Card</span>
                ) : (
                  <span>Expires: {new Date(card.expires_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Additional Info */}
        <div className="mt-8 text-left text-gray-400 text-sm"> {/* Changed text-center to text-left */}
          <p>This birthday card was created on {new Date(card.created_at).toLocaleDateString()} and has been viewed {card.views || 0} times.</p>
        </div>
      </div>
    </div>
  );
};

export default BirthdayCardViewer;